import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { ProjectDownloadIcon } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProjectDownloadIcon/ProjectDownloadIcon";
import { JointBillContext } from "@features/JointBillList/utils";
import { type ProjectTypeId } from "@features/Login/types";
import { Badge } from "@components/Badge/Badge";
import { IconButton, IconButtonLink } from "@components/IconButton/IconButton";
import { DownloadIcon, HouseIcon, PencilIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { type IOverviewOtherUserData } from "@services/overview/types";

interface IJointBillHeaderNameCellProps {
  houseDisplayName: string;
  otherUsers?: IOverviewOtherUserData[];
  planName: string;
  billStatusBadge?: JSX.Element | string;
  onChangePlanClick?: () => void;
  isEditHouseLoading?: boolean;
  onEditHouseClick?: () => void;
  isBillDownloadEnabled?: boolean;
  onBillDownloadClick?: () => void;
  isBillDownloadLoading?: boolean;
  projectTypeId?: ProjectTypeId;
  isProjectBillDownloadEnabled?: boolean;
  onProjectBillDownloadClick?: () => void;
  isProjectBillDownloadLoading?: boolean;
}

export const JointBillHeaderNameCell: React.FC<
  IJointBillHeaderNameCellProps
> = ({
  houseDisplayName,
  otherUsers,
  planName,
  billStatusBadge,
  onChangePlanClick,
  isEditHouseLoading,
  onEditHouseClick,
  isBillDownloadEnabled,
  onBillDownloadClick,
  isBillDownloadLoading,
  projectTypeId,
  isProjectBillDownloadEnabled,
  onProjectBillDownloadClick,
  isProjectBillDownloadLoading,
}) => {
  const { t } = useTranslation();
  const { isSummaryMode } = useContext(JointBillContext);

  const otherUsersLabel = otherUsers
    ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    .join(", ");

  return (
    <div className="flex items-center gap-4">
      <Badge
        icon={HouseIcon}
        size="large"
        variant="rounded"
        className="hidden bg-blue-dark md:flex"
        iconClassName="text-base-white"
      />
      <div className="flex flex-col gap-1">
        <div className="flex gap-2">
          <h4>{houseDisplayName}</h4>
          {!isSummaryMode && (
            <>
              <Tooltip content={t("bill.interactions.editAddress.tooltip")}>
                {(props) => (
                  <IconButton
                    {...props}
                    className="hidden md:block"
                    onClick={onEditHouseClick}
                    isLoading={isEditHouseLoading}
                    icon={PencilIcon}
                    iconClassName="size-4"
                    aria-label={t("action.edit")}
                  />
                )}
              </Tooltip>
              {isBillDownloadEnabled && (
                <Tooltip content={t("bill.interactions.downloadBill.tooltip")}>
                  {(props) => (
                    <IconButton
                      {...props}
                      className="hidden md:block"
                      onClick={onBillDownloadClick}
                      icon={DownloadIcon}
                      iconClassName="size-4"
                      isLoading={isBillDownloadLoading}
                      aria-label={t("bill.interactions.downloadBill.tooltip")}
                    />
                  )}
                </Tooltip>
              )}
              {isProjectBillDownloadEnabled && (
                <Tooltip
                  content={t(
                    `bill.interactions.downloadProjectBill.tooltips.${projectTypeId}`,
                  )}
                >
                  {(props) => (
                    <IconButton
                      {...props}
                      className="hidden md:block"
                      onClick={onProjectBillDownloadClick}
                      icon={(props) => (
                        <ProjectDownloadIcon
                          projectTypeId={projectTypeId}
                          {...props}
                        />
                      )}
                      iconClassName="size-4"
                      isLoading={isProjectBillDownloadLoading}
                      aria-label={t(
                        `bill.interactions.downloadProjectBill.tooltips.${projectTypeId}`,
                      )}
                    />
                  )}
                </Tooltip>
              )}
            </>
          )}
        </div>
        {!isSummaryMode && (
          <div className="hidden flex-row flex-wrap gap-x-2 md:flex">
            <div className="flex gap-2">
              <div className="text-sm">
                <span className="text-grey-dark">{`${t("bill.plan")}: `}</span>
                <span className="font-semibold">{planName}</span>
              </div>
              <Tooltip content={t("bill.interactions.changePlan.tooltip")}>
                {(props) => (
                  <IconButton
                    {...props}
                    className="mt-0.5 hidden self-start md:block"
                    onClick={onChangePlanClick}
                    icon={PencilIcon}
                    iconClassName="size-4"
                    aria-label={t("action.edit")}
                  />
                )}
              </Tooltip>
            </div>
            {!!otherUsers?.length && (
              <div className="flex w-full gap-2">
                <div className="flex max-w-full flex-nowrap text-sm md:max-w-[calc(100%-1.5rem)]">
                  <span className="mr-1 min-w-max text-grey-dark">{`${t(
                    "bill.anotherPayer",
                    {
                      count: otherUsers.length,
                    },
                  )}: `}</span>
                  <span className="truncate font-semibold">
                    {otherUsersLabel}
                  </span>
                </div>
                <Tooltip content={t("bill.interactions.editPayer.tooltip")}>
                  {(props) => (
                    <IconButtonLink
                      {...props}
                      className="hidden md:block"
                      href={`/settings/house`}
                      icon={PencilIcon}
                      iconClassName="size-4"
                      aria-label={t("bill.interactions.editPayer.tooltip")}
                    />
                  )}
                </Tooltip>
              </div>
            )}
          </div>
        )}
        <div className="md:hidden">{billStatusBadge}</div>
      </div>
    </div>
  );
};
